import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Printer, RefreshCw, TrendingUp, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { reportsAPI } from '../../lib/api';
import { HeadlineStrip } from '../../components/reports/leo-impact/HeadlineStrip';
import { ComparisonTable } from '../../components/reports/leo-impact/ComparisonTable';
import { ActivityLedger } from '../../components/reports/leo-impact/ActivityLedger';
import { SpendBreakdown } from '../../components/reports/leo-impact/SpendBreakdown';
import { OutcomeFunnel } from '../../components/reports/leo-impact/OutcomeFunnel';
import { AssumptionsPanel } from '../../components/reports/leo-impact/AssumptionsPanel';
import { formatLocal } from '../../lib/dateUtils';

const PERIODS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
  { days: 0, label: 'All time' },
];

export default function LeoImpactReport() {
  const [days, setDays] = useState(30);
  const [sdrMonthly, setSdrMonthly] = useState('');
  const [platformFee, setPlatformFee] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (sdrMonthly !== '') params.sdr_monthly_cost = Number(sdrMonthly);
      if (platformFee !== '') params.platform_fee_monthly = Number(platformFee);
      const res = await reportsAPI.getLeoImpact(params);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the LEO Impact report');
    } finally {
      setLoading(false);
    }
  }, [days, sdrMonthly, platformFee]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8 leo-impact-print" data-testid="leo-impact-report">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-emerald-600" /> LEO Impact — AI vs Human SDR
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every figure below is computed live from this workspace's own ledgers — calls, messages, meetings and the exact credits spent. Nothing is estimated except the clearly-labelled human benchmarks, which you can edit.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-full border border-border overflow-hidden" data-testid="leo-impact-period">
            {PERIODS.map(p => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                data-testid={`leo-impact-period-${p.days}`}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${days === p.days ? 'bg-foreground text-background' : 'hover:bg-muted'}`}
              >{p.label}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="leo-impact-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="leo-impact-print">
            <Printer className="h-4 w-4 mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {loading && !report ? (
        <div className="space-y-4" data-testid="leo-impact-loading">
          <Skeleton className="h-40" /><Skeleton className="h-64" /><Skeleton className="h-64" />
        </div>
      ) : report ? (
        <>
          <HeadlineStrip report={report} />
          <div className="grid gap-6 xl:grid-cols-5">
            <div className="xl:col-span-3"><ComparisonTable report={report} /></div>
            <div className="xl:col-span-2"><SpendBreakdown report={report} /></div>
          </div>
          <ActivityLedger report={report} />
          <OutcomeFunnel report={report} />
          <AssumptionsPanel
            report={report}
            sdrMonthly={sdrMonthly}
            platformFee={platformFee}
            onSdrMonthly={setSdrMonthly}
            onPlatformFee={setPlatformFee}
          />
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="leo-impact-footer">
            <ShieldCheck className="h-3.5 w-3.5" />
            Live data for {report.tenant_name || 'this workspace'} · generated {formatLocal(report.generated_at, 'MMM dd, yyyy HH:mm')} · period {report.period.since ? `${formatLocal(report.period.since, 'MMM dd, yyyy')} → ${formatLocal(report.period.until, 'MMM dd, yyyy')}` : 'all time'}
          </p>
        </>
      ) : null}
    </div>
  );
}
